import { Component } from '@theme/component';

/**
 * MasPatitas "Solo / Pack" choice on a product page.
 *
 * The radio group itself works without JavaScript: CSS (:has) shows the pack button or Horizon's
 * add to cart button for the checked option. This component only keeps the sticky add to cart bar
 * of the same section in sync (title, subtitle and button label from the data attributes) and, in
 * pack mode, sends the bar's button to the pack button (`mp-pack-button`), so the bar adds the pack
 * with its discount code instead of the product alone.
 *
 * @extends {Component}
 */
class MpOfferSelector extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('change', this.#handleChange, { signal });
    // Window, capture phase: runs before the framework's delegated click handlers, so the bar's
    // own action (Horizon's add to cart) never runs in pack mode
    window.addEventListener('click', this.#handleStickyClick, { capture: true, signal });
    this.#apply(this.#currentMode());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @returns {string} */
  #currentMode() {
    const checked = this.querySelector('input[type="radio"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : 'solo';
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    this.#apply(target.value);
  };

  /** @returns {Element | null} */
  #section() {
    return this.closest('.shopify-section');
  }

  /** @param {string} mode */
  #apply(mode) {
    this.dataset.mode = mode;
    const sticky = this.#section()?.querySelector('sticky-add-to-cart.mp-sticky-v3');
    if (!(sticky instanceof HTMLElement)) return;

    const isPack = mode === 'pack';
    const title = sticky.querySelector('.sticky-add-to-cart__title');
    const subtitle = sticky.querySelector('.sticky-add-to-cart__variant');
    const label = sticky.querySelector('.add-to-cart-text__content > span:first-child');

    if (title) title.textContent = (isPack ? this.dataset.packTitle : this.dataset.soloTitle) ?? '';
    if (subtitle) subtitle.textContent = (isPack ? this.dataset.packSub : this.dataset.soloSub) ?? '';
    if (label) label.textContent = (isPack ? this.dataset.packCta : this.dataset.soloCta) ?? '';
    sticky.dataset.mpMode = mode;
  }

  /** @param {MouseEvent} event */
  #handleStickyClick = (event) => {
    if (this.#currentMode() !== 'pack') return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    const section = this.#section();
    const stickyButton = target.closest('.mp-sticky-v3 .sticky-add-to-cart__button');
    if (!section || !stickyButton || !section.contains(stickyButton)) return;

    const packLink = section.querySelector('.mp-buy__pack mp-pack-button a');
    if (!(packLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    packLink.click();
  };
}

if (!customElements.get('mp-offer-selector')) {
  customElements.define('mp-offer-selector', MpOfferSelector);
}

/* "1 / 4" counter over the product photo (gallery setting "mp_counter"): follows the slideshow */
if (!window.mpMediaCounterReady) {
  window.mpMediaCounterReady = true;
  document.addEventListener('slideshow:select', (event) => {
    const gallery = event.target instanceof Element ? event.target.closest('media-gallery') : null;
    const counter = gallery?.parentElement?.querySelector(':scope > [data-mp-media-counter]');
    if (!(counter instanceof HTMLElement)) return;
    const detail = /** @type {{ index?: number } | undefined} */ (/** @type {CustomEvent} */ (event).detail);
    const index = typeof detail?.index === 'number' ? detail.index : 0;
    counter.textContent = `${index + 1} / ${counter.dataset.total}`;
  });
}
